import {
  Box,
  Checkbox,
  Flex,
  Heading,
  List,
  ListItem,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import React, { useCallback, useMemo } from "react";

import { EnergyLabel } from "../elements/EnergyLabel";
import { NumericCheckboxIcon } from "../elements/NumericCheckboxIcon";
import PriceDisplay from "../elements/PriceDisplay";
import { selectedScenariosAtom } from "../shared/state/atoms";
import { getCremNormYear } from "../shared/utils/cremNormUtils";
import { DifferenceBadge } from "./DifferenceBadge";
import { InfoPopover } from "./InfoPopover";
import {
  electricityPriceAtom,
  extraCostsAtom,
  gasPriceAtom,
  joulePriceAtom,
} from "../compositions/modals/SettingsModal";
import { ScenarioModel, SituationModel } from "../clients/KompasApi";
import { extractValue, hasMeasure } from "../shared/utils/measureUtils";
import { TextPopover } from "./TextPopover";
import { ScenarioTags } from "./ScenarioTags";

function Norm({ children }: React.PropsWithChildren) {
  return (
    <Box
      _before={{
        pos: "absolute",
        w: 1,
        h: 1,
        content: "''",
        display: "block",
        bg: "black",
        borderRadius: "50%",
        left: -3,
        top: 2.5,
      }}
      display="inline-block"
      ml={6}
      mr={2}
      position="relative"
    >
      {children}
    </Box>
  );
}

const measureTagPrefixDict: Map<keyof ScenarioModel, string> = new Map([
  ["energyPerformanceFeeCategoryName", "EPV: "],
  ["glazing", "Glas: "],
  ["facadeInsulation", "Gevel: "],
  ["gableEndInsulation", "Kopgevel: "],
  ["floorInsulation", "Vloerisolatie: "],
  ["roofInsulation", "Dakisolatie: "],
  ["ventilationSystem", "Ventilatie: "],
  ["centralHeatingSource", "CV: "],
  ["centralHeatingSupplyTemperature", "CV Temp.: "],
]);

interface ScenarioCardProps {
  situation: SituationModel;
  scenario: ScenarioModel;
}

export function ScenarioCard({ situation, scenario }: ScenarioCardProps) {
  const [gasPrice] = useAtom(gasPriceAtom);
  const [electricityPrice] = useAtom(electricityPriceAtom);
  const [joulePrice] = useAtom(joulePriceAtom);
  const [extraCosts] = useAtom(extraCostsAtom);
  const [selectedScenarios, setSelectedScenarios] = useAtom(
    selectedScenariosAtom,
  );

  const toggleSelectedScenario = useCallback(() => {
    if (selectedScenarios.includes(scenario.id)) {
      setSelectedScenarios(selectedScenarios.filter((e) => e !== scenario.id));
    } else {
      setSelectedScenarios([...selectedScenarios, scenario.id]);
    }
  }, [scenario.id, selectedScenarios, setSelectedScenarios]);

  const getEpvPrice = useCallback(
    (energyPerformanceFeeCategoryName?: string | null) => {
      if (!energyPerformanceFeeCategoryName) return 0;

      return extraCosts[energyPerformanceFeeCategoryName] ?? 0;
    },
    [extraCosts],
  );

  const defaultScenarioMonthlyCosts = useMemo(() => {
    const gasCosts =
      ((situation.calculation.yearlyGasConsumption ?? 0) * gasPrice) / 12;
    const electricityCosts =
      ((situation.calculation.yearlyElectricityConsumption ?? 0) *
        electricityPrice) /
      12;

    const heatNetworkUsage = 0; // scenario.calculation.heatNetworkUsage ?? 0;
    const jouleCosts = (joulePrice * heatNetworkUsage) / 12;

    const epvPrice = getEpvPrice(situation.energyPerformanceFeeCategoryName);
    const epvCosts =
      (situation.calculation.usableArea ??
        situation.calculation.lossArea ??
        0) * epvPrice;

    return gasCosts + electricityCosts + jouleCosts + epvCosts;
  }, [electricityPrice, gasPrice, joulePrice, getEpvPrice, situation]);

  const scenarioMonthlyCosts = useMemo(() => {
    const gasCosts =
      ((scenario.calculation.yearlyGasConsumption ?? 0) * gasPrice) / 12;
    const electricityCosts =
      ((scenario.calculation.yearlyElectricityConsumption ?? 0) *
        electricityPrice) /
      12;

    const heatNetworkUsage = 0; // scenario.calculation.heatNetworkUsage ?? 0;
    const jouleCosts = (joulePrice * heatNetworkUsage) / 12;

    const epvPrice = getEpvPrice(scenario.energyPerformanceFeeCategoryName);
    const epvCosts =
      (scenario.calculation.usableArea ?? scenario.calculation.lossArea ?? 0) *
      epvPrice;

    return gasCosts + electricityCosts + jouleCosts + epvCosts;
  }, [electricityPrice, gasPrice, joulePrice, getEpvPrice, scenario]);

  const norms = useMemo(() => {
    if (!scenario.calculation)
      return {
        crem: getCremNormYear(1000),
        gasloosReady: false,
        deStandaard: false,
      };
    return {
      crem: getCremNormYear(
        scenario.calculation.yearlyCO2EmissionPerUsableArea!,
      ),
      gasloosReady:
        scenario.calculation.heatDemand <=
        scenario.calculation.deStandaardThresholdValue,
      deStandaard: scenario.calculation.deStandaardExceedance <= 0,
    };
  }, [scenario.calculation]);

  const tagValues = useMemo(
    () =>
      Array.from(
        new Set(
          [
            scenario.calculation.sustainableEnergyGeneration && "PV",
            ...Array.from(measureTagPrefixDict)
              .filter(([measure]) =>
                hasMeasure(measure, [scenario], situation, false),
              )
              .map(
                ([measure, tagPrefix]) =>
                  `${tagPrefix}${extractValue(scenario[measure])}`,
              ),
          ].filter((v): v is string => !!v),
        ),
      ),
    [scenario, situation],
  );

  return (
    <Flex
      bg="white"
      border="1px solid"
      borderColor="brand.gray.100"
      borderRadius={4}
      boxShadow="0px 2px 4px 0px rgba(25, 25, 25, 0.04)"
      gap={4}
      p={4}
      w="100%"
    >
      <Flex flex={1} flexDir="column" gap={2}>
        <Heading fontWeight="bold" mt={1} size="sm">
          {scenario.name}
        </Heading>
        <List fontSize="small" color="brand.gray.120">
          {norms.crem && (
            <ListItem>
              <Norm>
                <TextPopover popoverSize="md" trigger="CRREM norm referentiejaar:">
                  CRREM staat voor Carbon Risk Real Estate Monitor. Het is een
                  initiatief dat gericht is op energiebesparingen in gebouwen om
                  te voldoen aan de klimaatdoelstellingen van het Klimaatakkoord
                  van Parijs. Deze norm is geadopteerd door grote
                  vastgoedbeleggers. De normweergave omvat het ambitiejaar met
                  daaronder de CO2-uitstoot per vierkante meter bruto
                  vloeroppervlak (gbo)
                </TextPopover>{" "}
                {norms.crem}
              </Norm>
            </ListItem>
          )}
          {norms.gasloosReady && (
            <ListItem>
              <Norm>
                <TextPopover size="md" trigger="Gasloos ready">
                  Dit scenario is gasloos ready en kan worden voorzien van een
                  duurzame installatie op lage of middentemperatuur afgifte. De
                  netto warmtevraag is lager dan De Standaard grenswaarde.
                </TextPopover>
              </Norm>
            </ListItem>
          )}
          {norms.deStandaard && (
            <ListItem>
              <Norm>
                <TextPopover size="md" trigger="De Standaard">
                  Dit scenario voldoet aan De Standaard. Deze standaard geeft
                  aan wanneer uw woning goed genoeg is geïsoleerd om aardgasvrij
                  te worden.
                </TextPopover>
              </Norm>
            </ListItem>
          )}
        </List>
        <ScenarioTags tags={tagValues} />
      </Flex>

      <Flex alignItems="flex-end" flexDir="column" gap={2}>
        <Text fontWeight={500}>Energielabel</Text>
        <EnergyLabel label={scenario.calculation.energyLabel} />
        <Text fontWeight={500}>
          <TextPopover trigger="Maandlasten">
            Dit zijn geschatte maandlasten voor energie.
          </TextPopover>
        </Text>
        <Box>
          {scenarioMonthlyCosts && defaultScenarioMonthlyCosts ? (
            <Text fontSize={20} fontWeight="bold">
              <PriceDisplay price={scenarioMonthlyCosts} />
              <DifferenceBadge
                compareValue={scenarioMonthlyCosts}
                currentValue={defaultScenarioMonthlyCosts}
              />
            </Text>
          ) : (
            <>
              ?{" "}
              <InfoPopover>
                Geen prijzen beschikbaar, vul deze in via het tandwiel
                rechtsboven
              </InfoPopover>
            </>
          )}
        </Box>
        <Checkbox
          icon={
            <NumericCheckboxIcon
              index={selectedScenarios.indexOf(scenario.id) + 1}
            />
          }
          isChecked={selectedScenarios.includes(scenario.id)}
          mb={-2}
          mt="auto"
          onChange={toggleSelectedScenario}
        >
          Vergelijk
        </Checkbox>
      </Flex>
    </Flex>
  );
}
